import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Calendar, MapPin, Ticket as TicketIcon, Download, Share2, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function TicketView() {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrLoading, setQrLoading] = useState(true);

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/tickets/${ticketId}`);
        setTicket(response.data);
      } catch (error) {
        console.error('Error fetching ticket:', error);
        toast.error('Ticket not found');
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
  }, [ticketId]);

  useEffect(() => {
    const fetchQrCode = async () => {
      if (!ticketId) return;
      try {
        const response = await axios.get(`${API_URL}/api/tickets/${ticketId}/qr-code`);
        setQrCode(response.data);
      } catch (error) {
        console.error('Error fetching QR code:', error);
      } finally {
        setQrLoading(false);
      }
    };
    fetchQrCode();
  }, [ticketId]);

  const handleShare = async () => {
    const shareText = `I'm attending ${ticket.event_title}! Get your tickets on Next Gig.`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: ticket.event_title, text: shareText });
      } catch (shareError) {
        // AbortError is expected when user cancels the share dialog
        if (shareError.name !== 'AbortError') {
          console.error('Share failed:', shareError);
        }
      }
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success('Copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="h-8 w-32 skeleton rounded mb-8" />
          <div className="h-96 skeleton rounded-2xl" />
        </div>
      </Layout>
    );
  }

  if (!ticket) return null;

  const eventDate = new Date(ticket.event_date);
  const isPast = eventDate < new Date();
  const isUsed = qrCode?.is_used || ticket?.qr_used;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link
          to="/dashboard/tickets"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="back-to-tickets"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Tickets
        </Link>

        {/* Ticket Card */}
        <Card className="overflow-hidden">
          {/* Header */}
          <div className="relative bg-primary p-6">
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black/20 to-transparent" />
            <div className="relative">
              <Badge 
                className={`mb-3 ${isPast ? 'bg-white/20' : 'bg-secondary text-secondary-foreground'}`}
              >
                {isPast ? 'Past Event' : 'Valid Ticket'}
              </Badge>
              <h1 className="text-2xl font-bold text-white mb-2">{ticket.event_title}</h1>
              <p className="text-white/80">{ticket.ticket_type_name}</p>
            </div>
          </div>

          <CardContent className="p-6">
            {/* QR Code */}
            <div className="flex flex-col items-center mb-6">
              <div className="ticket-qr bg-white p-4 rounded-xl shadow-sm border">
                {qrLoading ? (
                  <div className="w-48 h-48 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : qrCode?.qr_image ? (
                  <div className="relative">
                    <img 
                      src={qrCode.qr_image} 
                      alt="Ticket QR Code" 
                      className={`w-48 h-48 ${isUsed ? 'opacity-30' : ''}`}
                    />
                    {isUsed && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-red-500/90 text-white px-4 py-2 rounded-lg font-bold transform -rotate-12">
                          USED
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-48 h-48 bg-muted flex items-center justify-center rounded-lg">
                    <div className="text-center">
                      <TicketIcon className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                      <p className="text-xs font-mono text-muted-foreground break-all">
                        {ticket.qr_code}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* QR Status */}
              <div className="mt-3 flex items-center gap-2">
                {isUsed ? (
                  <>
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span className="text-sm text-red-500 font-medium">
                      Ticket Used {qrCode?.used_at ? `on ${format(new Date(qrCode.used_at), 'MMM d, h:mm a')}` : ''}
                    </span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-green-500 font-medium">Valid - Show this QR code at entry</span>
                  </>
                )}
              </div>
              
              <p className="text-xs text-muted-foreground mt-2 text-center">
                This QR code can only be scanned once for entry
              </p>
            </div>

            {/* Ticket Details */}
            <div className="ticket-stub relative bg-muted/50 rounded-xl p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Date & Time</p>
                  <p className="font-medium">{format(eventDate, 'EEEE, MMMM d, yyyy • h:mm a')}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Venue</p>
                  <p className="font-medium">{ticket.venue || 'Check event details'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <TicketIcon className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Ticket ID</p>
                  <p className="font-mono text-sm">{ticket.ticket_id}</p>
                </div>
              </div>

              {ticket.price > 0 && (
                <div className="pt-4 border-t border-border">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Price Paid</span>
                    <span className="text-lg font-bold font-mono">
                      {(() => {
                        const sym = {'NGN':'₦','USD':'$','CAD':'CA$','GBP':'£','EUR':'€','GHS':'GH₵','KES':'KSh','ZAR':'R'}[ticket.currency || 'NGN'] || '₦';
                        return `${sym}${ticket.price.toLocaleString()}`;
                      })()}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={handleShare}
                data-testid="share-ticket-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Link to={`/events/${ticket.event_id}`} className="flex-1">
                <Button className="w-full btn-primary" data-testid="view-event-btn">
                  View Event
                </Button>
              </Link>
            </div>

            {/* Attendee Info */}
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground mb-1">Ticket Holder</p>
              <p className="font-medium">{ticket.user_name}</p>
              <p className="text-sm text-muted-foreground">{ticket.user_email}</p>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="mt-6">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-2">How to use your ticket</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Present this ticket at the event entrance</li>
              <li>• You can show the QR code on your phone or print it</li>
              <li>• Each ticket can only be scanned once</li>
              <li>• Arrive at least 30 minutes before the event starts</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
